// frontend/src/components/dashboard/PeakUsagePeriods.js
import React, { useState, useMemo, useEffect } from 'react';
import {
  Wrench,
  Zap,
  CalendarClock,
  Moon,
  Clock,
  Activity,
  Server,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import PillToggle from '../UI/PillToggle';
import { useUsageBehavior } from '../../hooks/useUsageAnalytics';

// ── Helpers ──────────────────────────────────────────────────────────────────
const HOURS = Array.from({ length: 24 }, (_, h) => h);

const formatNumber = (value) => Number(value || 0).toLocaleString();

// Compact hour label for dense axes/headers, e.g. 0 → "12a", 13 → "1p".
const hourCompact = (hour) => {
  const h = ((Number(hour) % 24) + 24) % 24;
  const suffix = h < 12 ? 'a' : 'p';
  const h12 = h % 12 || 12;
  return `${h12}${suffix}`;
};

// ── "Right now" in Singapore time ─────────────────────────────────────────────
// The whole analysis is computed in the company timezone (Asia/Singapore), so
// the live marker must be too — regardless of the admin's own browser timezone
// (an HR admin could be travelling). We resolve the SGT weekday + hour directly
// via Intl rather than assuming the viewer sits at UTC+8.
const SG_TZ = 'Asia/Singapore';

// Map Intl's short weekday name → ISO weekday (1=Mon … 7=Sun), matching the
// backend heatmap rows (ExtractIsoWeekDay).
const ISO_DOW_BY_SHORT = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

const clockLabel = (hour, minute) => {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const h12 = hour % 12 || 12;
  return `${h12}:${minute} ${suffix}`;
};

const getSingaporeNow = () => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SG_TZ,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  // hour12:false can emit "24" at midnight on some engines — fold back to 0..23.
  const hour = (parseInt(map.hour, 10) || 0) % 24;
  return {
    dow: ISO_DOW_BY_SHORT[map.weekday] ?? null,
    hour,
    minute: map.minute,
    label: `${map.weekday} ${clockLabel(hour, map.minute)}`,
  };
};

// Re-derive the SGT marker every minute so it stays accurate without a refresh.
const useSingaporeNow = () => {
  const [now, setNow] = useState(getSingaporeNow);
  useEffect(() => {
    const id = setInterval(() => setNow(getSingaporeNow()), 60 * 1000);
    return () => clearInterval(id);
  }, []);
  return now;
};

// Compact live "Now · Sat 10:47 PM SGT" pill with a pulsing dot.
const NowPill = ({ label }) => (
  <span
    className="inline-flex items-center gap-1.5 flex-shrink-0 rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-medium text-white"
    title="Current Singapore time"
  >
    <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
    </span>
    Now · {label} SGT
  </span>
);

// Map a 0..1 intensity ratio to a fixed Tailwind red shade so the JIT
// compiler can see every literal class (no dynamic shade interpolation).
const heatClass = (ratio) => {
  if (ratio <= 0) return 'bg-gray-100';
  if (ratio <= 0.2) return 'bg-red-100';
  if (ratio <= 0.35) return 'bg-red-200';
  if (ratio <= 0.5) return 'bg-red-300';
  if (ratio <= 0.65) return 'bg-red-400';
  if (ratio <= 0.82) return 'bg-red-500';
  return 'bg-red-600';
};

// Perceptual (square-root) intensity. A linear avg/maxAvg ratio lets a single
// spike wash out genuinely-busy cells, so we compress the top end and expand
// the low/mid range. Order is preserved (busiest cell stays darkest).
const intensity = (avg, maxAvg) => {
  if (!maxAvg || avg <= 0) return 0;
  return Math.sqrt(avg) / Math.sqrt(maxAvg);
};

const LEGEND_SWATCHES = ['bg-gray-100', 'bg-red-100', 'bg-red-200', 'bg-red-300', 'bg-red-400', 'bg-red-500', 'bg-red-600'];

const ACCENTS = {
  emerald: { card: 'bg-emerald-50 border-emerald-100', iconBg: 'bg-emerald-100', iconFg: 'text-emerald-700' },
  red: { card: 'bg-red-50 border-red-100', iconBg: 'bg-red-100', iconFg: 'text-red-600' },
  amber: { card: 'bg-white border-gray-200', iconBg: 'bg-amber-100', iconFg: 'text-amber-600' },
  indigo: { card: 'bg-white border-gray-200', iconBg: 'bg-indigo-100', iconFg: 'text-indigo-600' },
};

// ── Insight tile ─────────────────────────────────────────────────────────────
const InsightTile = ({ icon: Icon, accent, value, label, sub }) => (
  <div className={`rounded-2xl border p-4 sm:p-5 flex flex-col ${accent.card}`}>
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${accent.iconBg}`}>
        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${accent.iconFg}`} aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <p className="text-base sm:text-xl font-bold text-gray-900 leading-tight truncate">{value}</p>
        <p className="text-xs text-gray-500 truncate">{label}</p>
      </div>
    </div>
    {sub && <p className="text-xs text-gray-500 mt-3 leading-snug">{sub}</p>}
  </div>
);

const SEGMENT_FALLBACK = [
  { key: 'all', label: 'All' },
  { key: 'office', label: 'Staff' },
  { key: 'worker', label: 'Worker' },
];

const PeakUsagePeriods = ({ range, lifetimeContext = false }) => {
  const days = parseInt(range, 10) || 30;
  const [segment, setSegment] = useState('all');
  const [category, setCategory] = useState('all');
  const now = useSingaporeNow();

  const { data, isLoading, isError, isFetching } = useUsageBehavior(days, segment);

  const categories = useMemo(() => data?.categories || [], [data]);
  const active = useMemo(
    () => categories.find((c) => c.key === category) || categories[0] || null,
    [categories, category]
  );

  // Keep human (people) and system/integration categories in separate groups
  // so they read as two distinct lenses — never a single blended stream.
  const peopleOptions = categories
    .filter((c) => c.scope !== 'system')
    .map((c) => ({ id: c.key, label: c.label }));
  const systemOptions = categories
    .filter((c) => c.scope === 'system')
    .map((c) => ({ id: c.key, label: c.label }));
  const segmentOptions = (data?.segment_options || SEGMENT_FALLBACK).map((s) => ({
    id: s.key,
    label: s.label,
  }));

  const heatmap = active?.heatmap || [];
  const maxAvg = active?.heatmap_max_avg || 0;
  const insights = active?.insights || null;

  const hourData = useMemo(
    () => (active?.by_hour || []).map((pt) => ({ ...pt, label: hourCompact(pt.hour) })),
    [active]
  );

  const maintHour = insights?.maintenance_window?.start_hour;
  const peakHour = insights?.peak_hour?.hour;
  const isAll = active?.key === 'all';
  // System/integration categories (notifications, webhooks, jobs, uploads) are
  // portal-wide — the office/worker segment filter doesn't apply to them.
  const isSystem = active?.scope === 'system';

  // ── Loading / error gates ──────────────────────────────────────────────────
  if (isLoading) {
    /* Full section content load => skeleton (mirrors the section's real layout) */
    return (
      <section className="space-y-4 sm:space-y-6" aria-label="Employee behaviour patterns">
        {/* Header + segment toggle */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between animate-pulse">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <div className="w-6 h-6 bg-gray-200 rounded-full flex-shrink-0 mt-0.5"></div>
            <div className="space-y-2 min-w-0 flex-1">
              <div className="h-6 bg-gray-200 rounded w-56"></div>
              <div className="h-4 bg-gray-200 rounded w-full max-w-lg"></div>
            </div>
          </div>
          <div className="h-8 bg-gray-200 rounded-full w-full sm:w-48 flex-shrink-0"></div>
        </div>

        {/* Insight tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
              <div className="flex items-center">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                <div className="ml-2 sm:ml-4 space-y-2 min-w-0">
                  <div className="h-5 bg-gray-200 rounded w-20"></div>
                  <div className="h-3 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Heatmap card */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="h-40 bg-gray-100 rounded-lg"></div>
        </div>

        {/* Hour-of-day chart card */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-56 mb-4"></div>
          <div className="h-[220px] bg-gray-100 rounded-lg"></div>
        </div>
      </section>
    );
  }
  if (isError || !active) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 text-center py-12">
        <Activity className="w-14 h-14 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600">Behaviour analytics temporarily unavailable</p>
      </div>
    );
  }

  return (
    <section className="space-y-4 sm:space-y-6" aria-label="Employee behaviour patterns">
      {/* Heading + segment toggle. Toggle is full-width (segments spread evenly)
          on mobile, then collapses to its natural width and sits right-aligned
          beside the title on sm+ — the conventional placement for a section
          header control. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-2 min-w-0">
          <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h3 className="text-base sm:text-xl font-bold text-gray-900">Activity &amp; System Rhythm</h3>
            <p className="text-xs sm:text-sm text-gray-500">
              When people <span className="text-gray-400">and</span> systems interact with the
              portal — clock-ins, leave &amp; logins, plus notifications, webhooks &amp; background
              jobs — over the last {days} days (Singapore time).
              {lifetimeContext && (
                <> Totals above are since go-live; rhythm always uses this rolling window.</>
              )}{' '}
              Time deployments &amp; heavy jobs around the quiet windows.
            </p>
          </div>
        </div>
        {isSystem ? (
          <span
            className="inline-flex items-center gap-1.5 flex-shrink-0 rounded-full bg-indigo-50 border border-indigo-100 px-3 py-1.5 text-xs font-medium text-indigo-700"
            title="System & integration events are portal-wide and not split by workforce segment"
          >
            <Server className="w-3.5 h-3.5" aria-hidden="true" />
            Portal-wide
          </span>
        ) : (
          <PillToggle
            value={segment}
            onChange={setSegment}
            options={segmentOptions}
            variant="compact"
            theme="admin"
            layout="sectionHeader"
            ariaLabel="Workforce segment"
          />
        )}
      </div>

      {/* Interaction-type toggle — People vs System kept visually separate */}
      <div className="flex items-stretch gap-3 overflow-x-auto pb-1">
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" aria-hidden="true" /> People
          </span>
          <PillToggle
            value={active.key}
            onChange={setCategory}
            options={peopleOptions}
            variant="compact"
            theme="admin"
            ariaLabel="People interaction type"
          />
        </div>
        {systemOptions.length > 0 && (
          <>
            <span className="w-px bg-gray-200 self-stretch flex-shrink-0" aria-hidden="true" />
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-400 flex items-center gap-1">
                <Server className="w-3.5 h-3.5" aria-hidden="true" /> System
              </span>
              <PillToggle
                value={active.key}
                onChange={setCategory}
                options={systemOptions}
                variant="compact"
                theme="admin"
                ariaLabel="System &amp; integration event type"
              />
            </div>
          </>
        )}
      </div>
      <p className="text-xs text-gray-500 -mt-2">{active.description}</p>

      {/* Empty state for a category with no activity */}
      {(active.total_events || 0) === 0 || !insights ? (
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 text-center py-12">
          <Activity className="w-14 h-14 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">No {active.label.toLowerCase()} recorded in this period</p>
          <p className="text-sm text-gray-500 mt-2">
            {isSystem ? 'Try a wider date range' : 'Try a wider date range or a different segment'}
          </p>
        </div>
      ) : (
        <>
          {/* Insight tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <InsightTile
              icon={Zap}
              accent={ACCENTS.red}
              value={insights.peak_window?.label || 'N/A'}
              label="Typical peak window"
              sub={`Busiest 2-hour block · ~${formatNumber(insights.peak_window?.avg_per_hour)}/hr. Peak hour: ${insights.peak_hour?.label}.`}
            />
            <InsightTile
              icon={CalendarClock}
              accent={ACCENTS.amber}
              value={insights.busiest_day?.short || insights.busiest_day?.label || 'N/A'}
              label="Busiest day"
              sub={`~${formatNumber(insights.busiest_day?.avg)}/day. Quietest: ${insights.quietest_day?.label || 'N/A'} (~${formatNumber(insights.quietest_day?.avg)}/day).`}
            />
            <InsightTile
              icon={Moon}
              accent={ACCENTS.indigo}
              value={`${insights.after_hours_pct ?? 0}%`}
              label="After-hours share"
              sub={`Outside Mon–Fri 9am–6pm. Weekends alone: ${insights.weekend_pct ?? 0}%.`}
            />
            <InsightTile
              icon={Wrench}
              accent={ACCENTS.emerald}
              value={insights.maintenance_window?.label || 'N/A'}
              label="Quietest window"
              sub={
                isAll
                  ? `Safest 2-hour slot for deploys & maintenance (~${formatNumber(insights.maintenance_window?.avg_per_hour)}/hr).`
                  : `Lowest-activity 2-hour block (~${formatNumber(insights.maintenance_window?.avg_per_hour)}/hr).`
              }
            />
          </div>

          {/* Weekday × hour heatmap */}
          <div className={`bg-white rounded-2xl shadow-sm border p-4 sm:p-6 ${isFetching ? 'opacity-60 transition-opacity' : ''}`}>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-4">
              <div className="flex items-center gap-2 min-w-0">
                <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                  {active.label} — by Day &amp; Hour
                </h4>
                <NowPill label={now.label} />
              </div>
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-400">
                <span>Less</span>
                {LEGEND_SWATCHES.map((c) => (
                  <span key={c} className={`w-3 h-3 rounded-sm ${c}`} />
                ))}
                <span>More</span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <div className="min-w-[720px]">
                {/* Hour header (label every 3 hours; always label the live hour) */}
                <div className="flex items-center gap-px mb-1">
                  <div className="w-10 flex-shrink-0" />
                  {HOURS.map((h) => {
                    const isNowHour = h === now.hour;
                    return (
                      <div
                        key={h}
                        className={`flex-1 text-center text-[9px] ${
                          isNowHour ? 'text-slate-900 font-bold' : 'text-gray-400'
                        }`}
                      >
                        {isNowHour || h % 3 === 0 ? hourCompact(h) : ''}
                      </div>
                    );
                  })}
                </div>

                {/* One row per weekday */}
                {heatmap.map((row) => {
                  const isNowDay = row.dow === now.dow;
                  return (
                    <div key={row.dow} className="flex items-center gap-px mb-px">
                      <div
                        className={`w-10 flex-shrink-0 text-[11px] ${
                          isNowDay ? 'text-slate-900 font-bold' : 'font-medium text-gray-500'
                        }`}
                      >
                        {row.short}
                      </div>
                      {row.cells.map((cell) => {
                        const ratio = intensity(cell.avg, maxAvg);
                        const isNowCell = isNowDay && cell.hour === now.hour;
                        return (
                          <div
                            key={cell.hour}
                            className={`flex-1 h-6 rounded-sm ${heatClass(ratio)} ${
                              isNowCell ? 'relative z-10 ring-2 ring-slate-900' : ''
                            }`}
                            title={`${row.label} ${hourCompact(cell.hour)} — avg ${cell.avg}/hr (${formatNumber(cell.count)} total)${
                              isNowCell ? ' · happening now' : ''
                            }`}
                          >
                            {isNowCell && (
                              <span className="absolute inset-0 flex items-center justify-center">
                                <span className="h-1.5 w-1.5 rounded-full bg-slate-900 ring-1 ring-white" />
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
            <p className="text-[11px] text-gray-400 mt-3">
              Cell shade = average {active.label.toLowerCase()} per hour for that weekday slot
              (square-root scaled so busy mornings &amp; evenings stay visible next to peaks).
              The outlined cell marks the current Singapore day &amp; hour.
            </p>
          </div>

          {/* Hour-of-day profile */}
          <div className={`bg-white rounded-2xl shadow-sm border p-4 sm:p-6 ${isFetching ? 'opacity-60 transition-opacity' : ''}`}>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-1">
              <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                {active.label} — Hour-of-Day Profile
              </h4>
              <NowPill label={now.label} />
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Average per hour across all days.
              <span className="inline-flex items-center gap-1 ml-2">
                <span className="w-2.5 h-2.5 rounded-sm bg-red-600 inline-block" /> peak
              </span>
              <span className="inline-flex items-center gap-1 ml-2">
                <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> quietest
              </span>
              <span className="inline-flex items-center gap-1 ml-2">
                <span className="w-2.5 h-2.5 rounded-sm bg-slate-900 inline-block" /> now
              </span>
            </p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={hourData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="label" stroke="#6b7280" tick={{ fontSize: 10 }} interval={0} minTickGap={0} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px' }}
                  formatter={(value) => [`${value} / day avg`, active.label]}
                />
                <ReferenceLine
                  x={hourCompact(now.hour)}
                  stroke="#0f172a"
                  strokeDasharray="3 3"
                  label={{ value: 'Now', position: 'top', fill: '#0f172a', fontSize: 10, fontWeight: 600 }}
                />
                <Bar dataKey="avg" radius={[3, 3, 0, 0]}>
                  {hourData.map((entry) => {
                    let fill = '#fca5a5';
                    if (entry.hour === peakHour) fill = '#dc2626';
                    else if (entry.hour === maintHour || entry.hour === maintHour + 1) fill = '#10b981';
                    const isNowBar = entry.hour === now.hour;
                    return (
                      <Cell
                        key={entry.hour}
                        fill={fill}
                        stroke={isNowBar ? '#0f172a' : undefined}
                        strokeWidth={isNowBar ? 2 : 0}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </section>
  );
};

export default PeakUsagePeriods;
